import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { catalogSelector } from '../app/configurator-selectors';
import { useConfiguratorState } from '../app/configurator-logic';
import { QuestionValueType, configuratorActions, questionValuesSelector } from '../app/configurator-slice';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import { CfgQuestionMaterial } from '../app/types/catalog-types';
import { IMAGE_TYPE, ThumbnailMenuList } from '../controls/thumbnail-menu';
import { blockSeparator } from '../controls/util-styles';
import { enumYesNoCtrlStyle, eyncCtrlStyle, eyncTitleStyle } from './configurator-q-enum';

export type MaterialQuestionInteraction = 'tag-selected' | 'material-selected' | 'color-selected';
export type MaterialsType = 'tag' | 'material' | 'color'

export const ConfiguratorMaterialQuestion = ({ question, step, selectedValue, implicitValue, onValueChange, onQuestionDataChange }: {
    question: CfgQuestionMaterial,
    step: number
    selectedValue: string | null
    implicitValue: string | null
    onValueChange: (value: string|null) => void
    onQuestionDataChange?: (data: any, interaction: MaterialQuestionInteraction) => void
}) => {

    const dispatch = useAppDispatch();
    const { materialTags, qualities, colors } = useAppSelector(catalogSelector);    

    const questionValue = useSelector(questionValuesSelector);
    const qd: QuestionValueType = questionValue[question.id];

    const [nullableCheck, setNullableCheck] = useState(selectedValue === null)
    const areTagsList = question?.tags ? question?.tags?.length > 1 : false

    const { changeCanGoNextStep } = useConfiguratorState();

    const updateQuestionsValues = (values: Object) => {
        const questionValues = {
            stepId: question.id,
            stepValues: values
        }
        dispatch(configuratorActions.setQuestionsValues(questionValues))
    }

    useEffect (() => {
        let materialValue = implicitValue ? implicitValue.split('/')[0] : undefined;
        let colorValue = implicitValue ? implicitValue : undefined
        
        materialValue = question?.material ?? materialValue
        let explicitTag: string | undefined;
        if(qd) {
            explicitTag = qd?.tag;
        } else {
            if(materialValue) {
                const material =  qualities.find(q => q.code === materialValue);
                if(material) {
                    explicitTag = question?.tags?.find(tag => material.tags.includes(tag));  
                }
            }
        }

        if((question?.nullable && !nullableCheck) || (!question?.nullable)){
            updateQuestionsValues({
                tag: explicitTag ?? (question?.tags ? question?.tags[0] : ""),
                material: qd?.material ?? materialValue,
                color: qd?.color ?? colorValue
            })
        }        

    },[selectedValue, question, nullableCheck])

    useEffect(()=>{
        const selectedMaterialTag = qualities.filter( quality => quality.code === qd?.material && (quality.tags.find((tag) => tag === qd?.tag)))
        
        if(step === 0 && (question?.nullable && nullableCheck) || 
            (areTagsList && (qd?.tag === "" || !qd?.tag)) || 
            (!areTagsList && !qd?.material) || (qd?.material && selectedMaterialTag.length === 0)){
                changeCanGoNextStep(false)
        } 
         else {
            if(step === 1 && areTagsList && !qd?.material){
                changeCanGoNextStep(false)
            } else {
                changeCanGoNextStep(true)
            }
        }
    },[qd, nullableCheck, step])

    const onNullChange = (checked: boolean) => {
        setNullableCheck(checked)
        onValueChange(checked ? null : (qd?.material!==undefined && qd?.color!==undefined) ? qd?.color : implicitValue)
    }

    const onClick = (code: string, type: MaterialsType) => {
        onQuestionDataChange && onQuestionDataChange({ [type]: code }, `${type}-selected`)
        updateQuestionsValues && updateQuestionsValues({[type]: code}) 
        type === "color" && onValueChange(code)
    }

    const colorListProps = () => {
        const qualityColors = colors.filter(color => {
            return color?.material === qd?.material;
        })
        const materialId = qd?.material;
        const materialName = question.material ? undefined : qualities.find(m => m.code === qd?.material)?.name ?? "";
        return {
            thumbnailList: qualityColors,
            onHandleSeletedThumb: onClick,
            materialName, materialId,
            type: IMAGE_TYPE.COLOR,
            selectedCode: qd?.color
        }
    }

    const materialListProps = () => {
        const material = qualities.filter(quality => quality.tags.includes(qd?.tag??""))
         return {
            thumbnailList:material, 
            onHandleSeletedThumb: onClick, 
            type:IMAGE_TYPE.MATERIAL, 
            selectedCode: qd?.material
        }
    }

    const tagListProps = () => {
        const tags = materialTags.filter(tag => question?.tags?.includes(tag.code))
         return {
            thumbnailList:tags, 
            onHandleSeletedThumb: onClick, 
            type:IMAGE_TYPE.TAG, 
            selectedCode:qd?.tag
        }
    }

    const NullableQuestion = () => (
        <div className={`${enumYesNoCtrlStyle} ${blockSeparator}`} role="presentation">
            <div className={eyncCtrlStyle} role="presentation">
                <input type="checkbox" checked={nullableCheck } id={`${question.id}-mat`} onChange={e => onNullChange(e.target.checked)}/>
            </div>
            <label className={eyncTitleStyle} htmlFor={`${question.id}-mat`}>{question.nullable}</label>
        </div>)


    if(step === 0) {
        return <>
            { question?.material && question?.nullable && <NullableQuestion/> }
            { question?.material && ((question?.nullable && !nullableCheck) || (!question?.nullable)) && <ThumbnailMenuList {...colorListProps()}/>}

            { !question?.material && question?.nullable && <NullableQuestion/> }
            { !question?.material && ((question?.nullable && !nullableCheck) || (!question?.nullable)) &&
            
                (areTagsList ? 
                        <ThumbnailMenuList {...tagListProps()}/> :
                        <ThumbnailMenuList {...materialListProps()}/>)
            } 
            
        </>
    } 
    if(step === 1 && areTagsList){
        return <ThumbnailMenuList {...materialListProps()}/>
    } else {
        return (
                <ThumbnailMenuList {...colorListProps()}/>   
        )
    }
}